import { __assign, __awaiter, __extends, __generator, __read, __spreadArray } from "tslib";
import { CartActions } from '../../actions/CartActions/CartActions';
import { AddToCartActionOption, APP_DEFINITION_ID, CartEvents, CURRENCY_HEADER_NAME, MULTI_LANG_HEADER_NAME, } from '@wix/wixstores-client-core';
import { DEFAULT_CART_OPTIONS, MiniCartEvents, SecondaryEvents, SPECS } from '../../constants';
import { createClient } from '@wix/sdk/client';
import * as currentCart from '@wix/auto_sdk_ecom_current-cart';
import { getLeanGQLCartFromSDK } from './getLeanGQLCartFromSDK';
import { BIService } from '../../apis/BIService';
import { AnalyticsService } from '../../apis/AnalyticsService';
import { deserialize, serialize } from './serialize';
import { getApiBaseUrl } from '../../utils/getWixApiBaseUrl';
var CACHE_KEY = 'CURRENT_CART';
var origin = 'current-cart-service';
var CURRENT_CART_WARMUP_DATA_KEY = 'CURRENT_CART';
var BaseCurrentCartService = /** @class */ (function () {
    function BaseCurrentCartService(_a) {
        var context = _a.context, apis = _a.apis;
        this.callbacks = [];
        this.apis = apis;
        this.siteStore = context.siteStore;
    }
    BaseCurrentCartService.prototype.onChange = function (callback) {
        this.callbacks.push(callback);
    };
    BaseCurrentCartService.prototype.runCallbacks = function () {
        this.callbacks.forEach(function (callback) { return callback(); });
    };
    return BaseCurrentCartService;
}());
export { BaseCurrentCartService };
var CurrentCartService = /** @class */ (function (_super) {
    __extends(CurrentCartService, _super);
    function CurrentCartService(props) {
        var _this = _super.call(this, props) || this;
        _this.shouldRefetchOnNextGet = false;
        _this.handleGQLCartActionsChange = function (eventOptions) {
            return _this.refresh({ eventOptions: eventOptions });
        };
        _this.cartActions = new CartActions({ siteStore: _this.siteStore, origin: origin, originatedInWorker: true, context: props.context }, _this.handleGQLCartActionsChange);
        _this.biService = new BIService({ siteStore: _this.siteStore, origin: origin });
        _this.analyticsService = new AnalyticsService({ siteStore: _this.siteStore });
        var wixClient = createClient({
            auth: {
                getAuthHeaders: function () {
                    var _a, _b;
                    var currencyHeader = _this.siteStore.getCurrencyHeader();
                    var multiLangHeader = _this.siteStore.getMultiLangHeader();
                    return {
                        headers: __assign(__assign({ Authorization: _this.siteStore.instanceManager.getInstance() }, (currencyHeader ? (_a = {}, _a[CURRENCY_HEADER_NAME] = currencyHeader, _a) : {})), (multiLangHeader ? (_b = {}, _b[MULTI_LANG_HEADER_NAME] = multiLangHeader, _b) : {})),
                    };
                },
            },
            modules: {
                currentCart: {
                    getCurrentCart: currentCart.getCurrentCart,
                    estimateCurrentCartTotals: currentCart.estimateCurrentCartTotals,
                    addToCurrentCart: currentCart.addToCurrentCart,
                    updateCurrentCart: currentCart.updateCurrentCart,
                    updateCurrentCartLineItemQuantity: currentCart.updateCurrentCartLineItemQuantity,
                    removeCouponFromCurrentCart: currentCart.removeCouponFromCurrentCart,
                    removeLineItemsFromCurrentCart: currentCart.removeLineItemsFromCurrentCart,
                    createCheckoutFromCurrentCart: currentCart.createCheckoutFromCurrentCart,
                },
            },
            host: {
                apiBaseUrl: getApiBaseUrl(_this.siteStore.isSSR(), _this.siteStore.location.baseUrl),
            },
        });
        _this.currentCartApi = wixClient.currentCart;
        return _this;
    }
    CurrentCartService.prototype.init = function () {
        void this.getCurrentCart().catch(function () {
            //
        });
        this.listenToCurrentCartEvents();
        this.listenToSecondaryCurrentCartServiceRequests();
    };
    CurrentCartService.prototype.getCurrentCartInternal = function (_a) {
        var shouldWarmupData = _a.shouldWarmupData, skipMiniCartUpdate = _a.skipMiniCartUpdate, eventOptions = _a.eventOptions;
        return __awaiter(this, void 0, void 0, function () {
            var cart;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(!this.cartPromise || this.shouldRefetchOnNextGet)) return [3 /*break*/, 3];
                        this.cartPromise = this.getCurrentCartFromCacheOrServer({ shouldWarmupData: shouldWarmupData });
                        this.cartPromise.catch(function () {
                            _this.shouldRefetchOnNextGet = true;
                        });
                        if (!this.shouldRefetchOnNextGet) return [3 /*break*/, 3];
                        this.shouldRefetchOnNextGet = false;
                        return [4 /*yield*/, this.cartPromise];
                    case 1:
                        cart = _b.sent();
                        return [4 /*yield*/, this.notifyChange({ skipMiniCartUpdate: skipMiniCartUpdate, eventOptions: eventOptions, cart: cart })];
                    case 2:
                        _b.sent();
                        _b.label = 3;
                    case 3: return [2 /*return*/, this.cartPromise];
                }
            });
        });
    };
    CurrentCartService.prototype.getCurrentCart = function (_a) {
        var _b = _a === void 0 ? {} : _a, shouldWarmupData = _b.shouldWarmupData;
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_c) {
                return [2 /*return*/, this.getCurrentCartInternal({ shouldWarmupData: shouldWarmupData })];
            });
        });
    };
    CurrentCartService.prototype.estimateCurrentCartTotals = function (options) {
        /*istanbul ignore next */
        if (!this.currentCartApi) {
            throw new Error('Only supported with sdk');
        }
        return this.currentCartApi.estimateCurrentCartTotals(options);
    };
    CurrentCartService.prototype.getCurrentCartGQL = function (options) {
        if (options === void 0) { options = DEFAULT_CART_OPTIONS; }
        var withShipping = options.withShipping, withTax = options.withTax, shouldSyncWithCurrentCart = options.shouldSyncWithCurrentCart;
        if (withShipping !== DEFAULT_CART_OPTIONS.withShipping || withTax !== DEFAULT_CART_OPTIONS.withTax) {
            if (shouldSyncWithCurrentCart) {
                void this.refresh();
            }
            return this.cartActions.getCurrentCart({ withShipping: withShipping, withTax: withTax, locale: this.siteStore.storeLanguage });
        }
        return this.getCurrentCartGQLFromServer();
    };
    CurrentCartService.prototype.refresh = function (_a) {
        var _b = _a === void 0 ? {} : _a, skipMiniCartUpdate = _b.skipMiniCartUpdate, eventOptions = _b.eventOptions;
        this.invalidateCurrentCartCache();
        this.shouldRefetchOnNextGet = true;
        this.getCurrentCartInternal({ skipMiniCartUpdate: skipMiniCartUpdate, eventOptions: eventOptions }).catch(function () {
            //
        });
        return Promise.resolve();
    };
    CurrentCartService.prototype.updateLineItemQuantity = function (_a, biProps) {
        var lineItemId = _a.lineItemId, quantity = _a.quantity;
        return __awaiter(this, void 0, void 0, function () {
            var cart;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.currentCartApi.updateCurrentCartLineItemQuantity([{ _id: lineItemId, quantity: quantity }])];
                    case 1:
                        cart = (_b.sent()).cart;
                        void this.biService.updateCartItemQuantity({ origin: biProps === null || biProps === void 0 ? void 0 : biProps.origin, cart: cart });
                        return [2 /*return*/, this.updateCache(cart)];
                }
            });
        });
    };
    CurrentCartService.prototype.createCheckout = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.currentCartApi.createCheckoutFromCurrentCart(options)];
            });
        });
    };
    CurrentCartService.prototype.removeLineItem = function (_a, biProps) {
        var lineItemId = _a.lineItemId;
        return __awaiter(this, void 0, void 0, function () {
            var previousCart, cart;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.getCurrentCart()];
                    case 1:
                        previousCart = _b.sent();
                        return [4 /*yield*/, this.currentCartApi.removeLineItemsFromCurrentCart([lineItemId])];
                    case 2:
                        cart = (_b.sent()).cart;
                        void this.biService.removeLineItem({ cart: cart, origin: biProps === null || biProps === void 0 ? void 0 : biProps.origin });
                        this.analyticsService.trackRemoveLineItem({ lineItemId: lineItemId, updatedCart: cart, previousCart: previousCart });
                        return [2 /*return*/, this.updateCache(cart)];
                }
            });
        });
    };
    CurrentCartService.prototype.updateBuyerNote = function (_a) {
        var buyerNote = _a.buyerNote;
        return __awaiter(this, void 0, void 0, function () {
            var updatedCart;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.currentCartApi.updateCurrentCart({ cartInfo: { buyerNote: buyerNote } })];
                    case 1:
                        updatedCart = _b.sent();
                        return [2 /*return*/, this.updateCache(updatedCart)];
                }
            });
        });
    };
    CurrentCartService.prototype.applyCoupon = function (_a, biProps) {
        var code = _a.code;
        return __awaiter(this, void 0, void 0, function () {
            var updatedCart;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.currentCartApi.updateCurrentCart({ couponCode: code })];
                    case 1:
                        updatedCart = _b.sent();
                        void this.biService.applyCoupon({ cart: updatedCart, couponCode: code, origin: biProps === null || biProps === void 0 ? void 0 : biProps.origin });
                        return [2 /*return*/, this.updateCache(updatedCart)];
                }
            });
        });
    };
    CurrentCartService.prototype.removeCoupon = function (biProps) {
        return __awaiter(this, void 0, void 0, function () {
            var appliedDiscounts, cart;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getCurrentCart()];
                    case 1:
                        appliedDiscounts = (_a.sent()).appliedDiscounts;
                        return [4 /*yield*/, this.currentCartApi.removeCouponFromCurrentCart()];
                    case 2:
                        cart = (_a.sent()).cart;
                        void this.biService.removeCoupon({
                            origin: biProps === null || biProps === void 0 ? void 0 : biProps.origin,
                            discounts: appliedDiscounts,
                            cart: cart,
                        });
                        return [2 /*return*/, this.updateCache(cart)];
                }
            });
        });
    };
    CurrentCartService.prototype.setAddress = function (_a) {
        var address = _a.address;
        return __awaiter(this, void 0, void 0, function () {
            var country, subdivision, zipCode, cart;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        country = address.country, subdivision = address.subdivision, zipCode = address.zipCode;
                        return [4 /*yield*/, this.currentCartApi.updateCurrentCart({
                                cartInfo: {
                                    contactInfo: {
                                        address: {
                                            country: country,
                                            subdivision: subdivision !== null && subdivision !== void 0 ? subdivision : null,
                                            postalCode: zipCode !== null && zipCode !== void 0 ? zipCode : null,
                                            addressLine1: null,
                                            addressLine2: null,
                                            city: null,
                                            streetAddress: null,
                                        },
                                    },
                                },
                            })];
                    case 1:
                        cart = _b.sent();
                        return [4 /*yield*/, this.updateCache(cart)];
                    case 2:
                        _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    CurrentCartService.prototype.selectShippingOption = function (_a) {
        var _b;
        var selectedShippingOption = _a.selectedShippingOption, option = _a.option;
        return __awaiter(this, void 0, void 0, function () {
            var updatedCart;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0: return [4 /*yield*/, this.currentCartApi.updateCurrentCart({
                            cartInfo: {
                                selectedShippingOption: {
                                    code: (_b = option === null || option === void 0 ? void 0 : option.code) !== null && _b !== void 0 ? _b : selectedShippingOption.optionId,
                                    carrierId: option === null || option === void 0 ? void 0 : option.carrierId,
                                },
                            },
                        })];
                    case 1:
                        updatedCart = _c.sent();
                        return [2 /*return*/, this.updateCache(updatedCart)];
                }
            });
        });
    };
    CurrentCartService.prototype.addToCurrentCart = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var cart;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.currentCartApi.addToCurrentCart(options)];
                    case 1:
                        cart = (_a.sent()).cart;
                        if (!cart) {
                            throw new Error('Failed to Add To Current Cart');
                        }
                        return [2 /*return*/, this.updateCache(cart)];
                }
            });
        });
    };
    CurrentCartService.prototype.notifyChange = function (_a) {
        var skipMiniCartUpdate = _a.skipMiniCartUpdate, eventOptions = _a.eventOptions, cart = _a.cart;
        this.runCallbacks();
        this.publishUpdateForSecondaryCurrentCart();
        if (!skipMiniCartUpdate) {
            return this.updateMiniCart(eventOptions, cart);
        }
    };
    CurrentCartService.prototype.listenToSecondaryCurrentCartServiceRequests = function () {
        var _this = this;
        this.subscribeToSecondaryEvent(SecondaryEvents.GetGQLRequest, this.getCurrentCartGQL.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.GetRequest, this.getCurrentCart.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.RefreshRequest, this.refresh.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.UpdateQuantityRequest, this.updateLineItemQuantity.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.RemoveItemRequest, this.removeLineItem.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.UpdateBuyerNoteRequest, this.updateBuyerNote.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.ApplyCouponRequest, this.applyCoupon.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.RemoveCouponRequest, this.removeCoupon.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.SetAddressRequest, this.setAddress.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.SelectShippingOptionRequest, this.selectShippingOption.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.EstimateCurrentCartTotals, this.estimateCurrentCartTotals.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.AddToCurrentCart, this.addToCurrentCart.bind(this));
        this.subscribeToSecondaryEvent(SecondaryEvents.CreateCheckoutRequest, this.createCheckout.bind(this));
        this.apis.pubSub.subscribe(SecondaryEvents.HEALTH_CHECK, function () {
            _this.apis.pubSub.publish(SecondaryEvents.ACK, {}, false);
        }, false);
    };
    CurrentCartService.prototype.subscribeToSecondaryEvent = function (event, performAction) {
        var _this = this;
        this.apis.pubSub.subscribe(event, 
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        function (_a) {
            var data = _a.data;
            return __awaiter(_this, void 0, void 0, function () {
                var _b, key, payload, actionResponse, error, e_1;
                return __generator(this, function (_c) {
                    switch (_c.label) {
                        case 0:
                            _b = deserialize(data), key = _b.key, payload = _b.payload;
                            _c.label = 1;
                        case 1:
                            _c.trys.push([1, 3, , 4]);
                            return [4 /*yield*/, performAction.apply(void 0, __spreadArray([], __read((payload || [])), false))];
                        case 2:
                            actionResponse = _c.sent();
                            return [3 /*break*/, 4];
                        case 3:
                            e_1 = _c.sent();
                            actionResponse = undefined;
                            error = e_1;
                            return [3 /*break*/, 4];
                        case 4:
                            this.apis.pubSub.publish(SecondaryEvents.ActionResponse, serialize({ key: key, payload: actionResponse, error: error }), false);
                            return [2 /*return*/];
                    }
                });
            });
        }, false);
    };
    // eslint-disable-next-line sonarjs/cognitive-complexity
    CurrentCartService.prototype.listenToCurrentCartEvents = function () {
        var _this = this;
        var currency = this.siteStore.location.query.currency;
        this.siteStore.location.onChange(function () {
            if (currency !== _this.siteStore.location.query.currency) {
                currency = _this.siteStore.location.query.currency;
                void _this.refresh();
            }
        });
        this.siteStore.pubSubManager.subscribe(CartEvents.CLEARED, function (res) { return void _this.clearCart(res.data.cartId); });
        this.siteStore.pubSubManager.subscribe(CartEvents.CHANGED, function (res) {
            var _a;
            if ((_a = res.data.eventOptions) === null || _a === void 0 ? void 0 : _a.originatedInWorker) {
                return;
            }
            void _this.refresh({ skipMiniCartUpdate: true });
        });
        this.siteStore.pubSubManager.subscribe(CartEvents.INVALIDATED, function () {
            _this.invalidateCurrentCartCache();
        });
        this.siteStore.siteApis.onInstanceChanged(function () { return void _this.refresh(); }, APP_DEFINITION_ID);
        this.siteStore.pubSubManager.subscribe(MiniCartEvents.LoadedWithoutData, function () {
            _this.getMiniCartLeanModel().then(function (cart) {
                _this.siteStore.pubSubManager.publish(MiniCartEvents.OnInitialData, cart);
            });
        });
    };
    CurrentCartService.prototype.getMiniCartLeanModel = function (cart) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, getLeanGQLCartFromSDK(this, cart)];
            });
        });
    };
    CurrentCartService.prototype.updateCache = function (cart) {
        this.cartPromise = Promise.resolve(cart);
        this.shouldRefetchOnNextGet = false;
        this.saveCartToMemoryStorage(cart);
        return this.notifyChange({ cart: cart });
    };
    CurrentCartService.prototype.invalidateCurrentCartCache = function () {
        this.apis.storage.memory.removeItem(CACHE_KEY);
    };
    CurrentCartService.prototype.publishUpdateForSecondaryCurrentCart = function () {
        this.siteStore.pubSubManager.publish(SecondaryEvents.Updated, {});
    };
    CurrentCartService.prototype.updateMiniCart = function (eventOptions, cart) {
        if (eventOptions === void 0) { eventOptions = {
            shouldOpenCart: false,
            addToCartActionOption: AddToCartActionOption.MINI_CART,
        }; }
        return __awaiter(this, void 0, void 0, function () {
            var _a, _b, _c, _d;
            return __generator(this, function (_e) {
                switch (_e.label) {
                    case 0:
                        _b = (_a = this.siteStore.pubSubManager).publish;
                        _c = [CartEvents.CHANGED];
                        _d = [{}];
                        return [4 /*yield*/, this.getMiniCartLeanModel(cart)];
                    case 1:
                        _b.apply(_a, _c.concat([__assign.apply(void 0, [__assign.apply(void 0, _d.concat([(_e.sent())])), { extraParams: { origin: 'CurrentCartUpdate' }, eventOptions: __assign(__assign({}, eventOptions), { originatedInWorker: true }), reason: 'CurrentCartUpdate' }])]));
                        return [2 /*return*/];
                }
            });
        });
    };
    CurrentCartService.prototype.clearCart = function (givenCartId) {
        return __awaiter(this, void 0, void 0, function () {
            var _id, cart;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getCurrentCart()];
                    case 1:
                        _id = (_a.sent())._id;
                        if (!(givenCartId === _id)) return [3 /*break*/, 3];
                        cart = { lineItems: [] };
                        this.cartPromise = Promise.resolve(cart);
                        this.shouldRefetchOnNextGet = false;
                        return [4 /*yield*/, this.notifyChange({ cart: cart })];
                    case 2:
                        _a.sent();
                        this.invalidateCurrentCartCache();
                        _a.label = 3;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    CurrentCartService.prototype.getCurrentCartFromCacheOrServer = function (_a) {
        var _b;
        var shouldWarmupData = _a.shouldWarmupData;
        return __awaiter(this, void 0, void 0, function () {
            var cart, cachedCart, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (!this.shouldRefetchOnNextGet) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.getCurrentCartFromServer()];
                    case 1:
                        cart = _d.sent();
                        return [3 /*break*/, 7];
                    case 2:
                        cachedCart = this.getCartFromMemoryStorage();
                        if (cachedCart) {
                            return [2 /*return*/, cachedCart];
                        }
                        return [4 /*yield*/, this.getWarmupCart()];
                    case 3:
                        if (!((_b = (_d.sent())) !== null && _b !== void 0)) return [3 /*break*/, 4];
                        _c = _b;
                        return [3 /*break*/, 6];
                    case 4: return [4 /*yield*/, this.getCurrentCartFromServer()];
                    case 5:
                        _c = (_d.sent());
                        _d.label = 6;
                    case 6:
                        cart = _c;
                        if (shouldWarmupData && this.siteStore.isSSR()) {
                            this.siteStore.windowApis.warmupData.set(CURRENT_CART_WARMUP_DATA_KEY, cart);
                        }
                        _d.label = 7;
                    case 7:
                        this.saveCartToMemoryStorage(cart);
                        return [2 /*return*/, cart];
                }
            });
        });
    };
    CurrentCartService.prototype.getCurrentCartGQLFromServer = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.cartActions.getCurrentCart(__assign(__assign({}, DEFAULT_CART_OPTIONS), { locale: this.siteStore.storeLanguage }))];
            });
        });
    };
    CurrentCartService.prototype.getCurrentCartFromServer = function () {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function () {
            var e_2;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.currentCartApi.getCurrentCart()];
                    case 1: return [2 /*return*/, _c.sent()];
                    case 2:
                        e_2 = _c.sent();
                        /*istanbul ignore next */
                        if (((_b = (_a = e_2 === null || e_2 === void 0 ? void 0 : e_2.details) === null || _a === void 0 ? void 0 : _a.applicationError) === null || _b === void 0 ? void 0 : _b.code) === 'OWNED_CART_NOT_FOUND') {
                            return [2 /*return*/, { lineItems: [] }];
                        }
                        throw e_2;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    CurrentCartService.prototype.saveCartToMemoryStorage = function (cart) {
        this.apis.storage.memory.setItem(CACHE_KEY, JSON.stringify(cart));
    };
    CurrentCartService.prototype.getCartFromMemoryStorage = function () {
        var cachedCart = this.apis.storage.memory.getItem(CACHE_KEY);
        return cachedCart ? JSON.parse(cachedCart) : undefined;
    };
    CurrentCartService.prototype.getWarmupCart = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.siteStore.experiments.enabled(SPECS.WaitForWarmupData)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.siteStore.windowApis.warmupData.waitForWarmupData(CURRENT_CART_WARMUP_DATA_KEY)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/, this.siteStore.windowApis.warmupData.get(CURRENT_CART_WARMUP_DATA_KEY)];
                }
            });
        });
    };
    return CurrentCartService;
}(BaseCurrentCartService));
export { CurrentCartService };
//# sourceMappingURL=CurrentCartService.js.map